import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { VStack, Text, Heading, Spinner, Button, Box } from '@chakra-ui/react';
import { useAuth } from '../AuthProvider';
import {
  socialFinish,
  SOCIAL_PROVIDER_KEY,
  type SocialProvider,
} from '../emailAuth';

const VALID_PROVIDERS: readonly SocialProvider[] = ['google', 'apple', 'facebook'];

function splitName(fullName: string | undefined): { firstName: string; lastName: string } {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

/**
 * Social-login return target (Zitadel IdP intent). The provider redirected the
 * browser here with the intent `id` + `token`; we hand them to POST
 * /auth/social/finish, which mints Streamko tokens. Then:
 *   - returning user → the app home (`/app`).
 *   - brand-new user (needsProfile) → `/signup` resumed at the profile step,
 *     pre-filled with the provider's email + name, to create their org.
 *
 * The chosen provider rode across the full-page redirect in sessionStorage
 * (see OAuthButtons). The intent token is single-use, so the exchange is guarded
 * to run exactly once even under React StrictMode's double-effect.
 */
export function IdpCallbackPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshAuth } = useAuth();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const provider = sessionStorage.getItem(SOCIAL_PROVIDER_KEY) as SocialProvider | null;
    sessionStorage.removeItem(SOCIAL_PROVIDER_KEY);

    // Zitadel names these `id` + `token`; accept the documented alternates too
    // (the exact param names are confirmed on-device).
    const intentId = params.get('id') ?? params.get('intentId');
    const token = params.get('token') ?? params.get('idpIntentToken');
    const failed = params.get('error');

    (async () => {
      if (failed || !intentId || !token) {
        setError(t('auth.socialLoginFailed'));
        return;
      }
      if (!provider || !VALID_PROVIDERS.includes(provider)) {
        setError(t('auth.socialLoginFailed'));
        return;
      }
      try {
        const data = await socialFinish({ provider, intentId, token });
        refreshAuth();
        if (data.needsProfile) {
          const { firstName, lastName } = splitName(data.user?.fullName);
          navigate('/signup', {
            replace: true,
            state: {
              resumeProfile: true,
              prefill: { email: data.user?.email, firstName, lastName },
            },
          });
        } else {
          navigate('/app', { replace: true });
        }
      } catch (err) {
        console.warn('[IdpCallback] social finish failed', err);
        setError(err instanceof Error ? err.message : t('auth.socialLoginFailed'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <Box minH="100vh" bg="onboarding.cream" display="flex" justifyContent="center" alignItems="center" p={4}>
        <VStack spacing={4} p={6} bg="white" borderRadius="lg" w="100%" maxW="420px" align="center" boxShadow="md">
          <Text fontSize="40px">⚠️</Text>
          <Heading size="md" textAlign="center" color="onboarding.plum">
            {t('auth.socialLoginFailedTitle')}
          </Heading>
          <Text textAlign="center" color="onboarding.mutedRose">{error}</Text>
          <Button onClick={() => navigate('/signup', { replace: true })} colorScheme="purple">
            {t('auth.backToSignIn')}
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="onboarding.cream" display="flex" justifyContent="center" alignItems="center">
      <VStack spacing={4}>
        <Spinner size="xl" color="onboarding.plum" />
        <Text color="onboarding.mutedRose">{t('auth.socialSigningIn')}</Text>
      </VStack>
    </Box>
  );
}

export default IdpCallbackPage;
